// v101.45.0 · Rellenar infraestructura de red (VLANs → IPs → relaciones VLAN↔equipo) desde Odoo.
// Corre en segundo plano en el servidor de producción; esta tarjeta muestra la vista previa y el avance.
import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2, Eye, Database, RotateCcw } from "lucide-react";

const ROWS = [
  { key: "vlans", label: "VLANs (operativas)" },
  { key: "ips", label: "IPs" },
  { key: "rels", label: "Relaciones VLAN↔equipo" },
];

function SnapshotTable({ snap, title }) {
  if (!snap) return null;
  return (
    <div className="rounded-lg bg-white border border-gray-200 overflow-hidden">
      <div className="px-3 py-1.5 text-[10px] font-bold text-gray-600 bg-gray-50 border-b border-gray-200">
        {title}{snap.at ? ` · ${snap.at}` : ""}
      </div>
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-gray-500">
            <th className="text-left px-3 py-1 font-semibold">&nbsp;</th>
            <th className="text-right px-3 py-1 font-semibold">Odoo</th>
            <th className="text-right px-3 py-1 font-semibold">AlfaField</th>
            <th className="text-right px-3 py-1 font-semibold">Faltan</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((r) => {
            const d = snap[r.key] || {};
            return (
              <tr key={r.key} className="border-t border-gray-100">
                <td className="px-3 py-1 font-semibold text-gray-700">{r.label}</td>
                <td className="px-3 py-1 text-right tabular-nums">{(d.odoo ?? 0).toLocaleString()}</td>
                <td className="px-3 py-1 text-right tabular-nums">{(d.alfafield ?? 0).toLocaleString()}</td>
                <td className={`px-3 py-1 text-right tabular-nums font-bold ${d.faltan ? "text-red-600" : "text-green-700"}`}>
                  {(d.faltan ?? 0).toLocaleString()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {snap.ips?.dependen_de_vlan_faltante > 0 && (
        <div className="px-3 py-1 text-[10px] text-amber-700 bg-amber-50 border-t border-amber-100">
          {snap.ips.dependen_de_vlan_faltante.toLocaleString()} IPs dependen de VLANs faltantes: se cargan al completar las VLANs.
        </div>
      )}
    </div>
  );
}

export default function InfraRefillCard() {
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const timer = useRef(null);

  const running = status?.status === "running" && !status?.stale;

  const fetchStatus = async () => {
    try {
      const { data } = await api.get("/v1/admin/infra-refill/status");
      setStatus(data);
      return data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => clearInterval(timer.current);
  }, []);

  useEffect(() => {
    clearInterval(timer.current);
    if (running) timer.current = setInterval(fetchStatus, 5000);
    return () => clearInterval(timer.current);
  }, [running]);

  const doPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.get("/v1/admin/infra-refill/preview", { timeout: 180000 });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo leer Odoo");
    } finally {
      setLoadingPreview(false);
    }
  };

  const doRun = async (completo) => {
    const msg = completo
      ? "RECARGA COMPLETA: se relee todo desde Odoo (las VLANs que Odoo ya no tiene operativas quedan inactivas). Puede tardar bastante. ¿Continuar?"
      : "Se traerán desde Odoo las VLANs, IPs y relaciones que faltan (no se borra nada, salvo relaciones que Odoo ya no tiene). ¿Continuar?";
    if (!window.confirm(msg)) return;
    setStarting(true);
    try {
      const { data } = await api.post(`/v1/admin/infra-refill/run${completo ? "?completo=true" : ""}`);
      toast.success(data?.message || "Relleno iniciado");
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el relleno");
    } finally {
      setStarting(false);
    }
  };

  const res = status?.result || {};
  return (
    <div className="rounded-xl border-2 border-[#0A3747]/20 bg-white p-4 space-y-3" data-testid="infra-refill-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-xs font-bold text-[#0A3747]">Rellenar infraestructura de red (VLANs → IPs → Relaciones)</h3>
          <p className="text-[10px] text-gray-500 mt-0.5 max-w-xl">
            Compara Odoo con AlfaField y trae lo que falta. Corre en segundo plano en el servidor: puede cerrar esta
            pantalla. Mientras corre, la sincronización automática de VLANs/IPs/relaciones espera.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={doPreview} disabled={loadingPreview || running}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
            data-testid="btn-infra-preview">
            {loadingPreview ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Vista previa
          </button>
          <button onClick={() => doRun(false)} disabled={starting || running}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-[#0A3747] hover:bg-[#135166] disabled:opacity-50"
            data-testid="btn-infra-run">
            {starting || running ? <Loader2 size={13} className="animate-spin" /> : <Database size={13} />} Rellenar lo que falta
          </button>
          <button onClick={() => doRun(true)} disabled={starting || running}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
            data-testid="btn-infra-run-full">
            <RotateCcw size={13} /> Recarga completa
          </button>
        </div>
      </div>

      {status && status.status !== "not_run" && (
        <div className={`rounded-lg px-3 py-2 text-[11px] border ${running ? "bg-blue-50 border-blue-200 text-blue-800"
          : status.status === "ok" ? "bg-green-50 border-green-200 text-green-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}
          data-testid="infra-refill-status">
          <b>{running ? "En curso" : status.stale ? "Interrumpido" : status.status === "ok" ? "Completado" : "Terminó con observaciones"}</b>
          {" · "}{status.progress || "—"}
          {res.duration_sec != null && !running ? ` · ${Math.round(res.duration_sec / 60)} min` : ""}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <SnapshotTable snap={preview} title="Vista previa (ahora)" />
        {!preview && <SnapshotTable snap={res.before} title="Antes del último relleno" />}
        <SnapshotTable snap={res.after} title="Después del último relleno" />
      </div>
    </div>
  );
}
